<style scoped>
.modal-dialog {
	z-index: 1060;
}

.modal-backdrop {
	z-index: 1050;
}
</style>

<template>
	<div
		v-if="modelValue"
		class="modal fade show d-block"
		tabindex="-1"
		@click.self="$emit('update:modelValue', false)"
	>
		<div class="modal-dialog modal-dialog-centered">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">
						<i class="bi bi-house me-2"></i>Property Details
					</h5>
					<button
						type="button"
						class="btn-close"
						@click="$emit('update:modelValue', false)"
					></button>
				</div>
				<div class="modal-body">
					<p v-if="!parcel && !situs" class="text-muted small mb-0">
						No property record available for this site.
					</p>

					<template v-if="parcel">
						<h6>Parcel</h6>
						<dl class="row mb-0">
							<template v-for="row in parcelRows" :key="row.label">
								<dt class="col-sm-5">{{ row.label }}</dt>
								<dd class="col-sm-7">{{ row.value }}</dd>
							</template>
						</dl>
					</template>

					<template v-if="situs">
						<h6>Assessment (Situs)</h6>
						<dl class="row mb-0">
							<template v-for="row in situsRows" :key="row.label">
								<dt class="col-sm-5">{{ row.label }}</dt>
								<dd class="col-sm-7">{{ row.value }}</dd>
							</template>
						</dl>
					</template>
				</div>
			</div>
		</div>
		<div class="modal-backdrop fade show"></div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { rawSitusRows } from "@/lib/situs";
import type { MunicipalParcel, Situs } from "@/type/api";

const props = defineProps<{
	modelValue: boolean;
	parcel?: MunicipalParcel | null;
	situs?: Situs | null;
}>();

defineEmits<{
	"update:modelValue": [value: boolean];
}>();

const parcelRows = computed(() => {
	const parcel = props.parcel;
	if (!parcel) return [];
	const rows: { label: string; value: string }[] = [];
	rows.push({ label: "APN", value: parcel.external_id || String(parcel.id) });
	if (parcel.address_raw) {
		rows.push({ label: "Address", value: parcel.address_raw });
	}
	if (parcel.description) {
		rows.push({ label: "Description", value: parcel.description });
	}
	if (parcel.property_type) {
		rows.push({ label: "Property type", value: parcel.property_type });
	}
	return rows;
});

interface SitusRowSpec {
	label: string;
	key: Exclude<keyof Situs, "raw_data">;
	format?: (value: string | number) => string;
}

const situsRowSpecs: SitusRowSpec[] = [
	{ label: "Address", key: "address_raw" },
	{
		label: "Lot size (sq ft)",
		key: "lot_size_sqft",
		format: (value) => `${value.toLocaleString()} sq ft`,
	},
	{
		label: "Lot size (acres)",
		key: "lot_size_acres",
		format: (value) => `${value.toLocaleString()} acres`,
	},
	{ label: "Year built", key: "year_built" },
	{ label: "Bedrooms", key: "bedrooms" },
	{ label: "Full baths", key: "full_baths" },
	{ label: "Half baths", key: "half_baths" },
	{
		label: "Building (sq ft)",
		key: "building_sqft",
		format: (value) => `${value.toLocaleString()} sq ft`,
	},
	{ label: "Units", key: "num_units" },
	{ label: "Property type", key: "property_type" },
	{
		label: "Net assessed value",
		key: "net_assessed_value",
		format: (value) => `$${value.toLocaleString()}`,
	},
	{
		label: "Land value",
		key: "land_value",
		format: (value) => `$${value.toLocaleString()}`,
	},
	{ label: "Fireplaces", key: "fireplace_count" },
	{ label: "Pools", key: "pool_count" },
	{ label: "Tax rate area", key: "tax_rate_area" },
];

const situsRows = computed(() => {
	const situs = props.situs;
	if (!situs) return [];
	const rows: { label: string; value: string }[] = [];
	for (const spec of situsRowSpecs) {
		const value = situs[spec.key];
		if (value == null) continue;
		rows.push({
			label: spec.label,
			value: spec.format ? spec.format(value) : String(value),
		});
	}
	// Append the source's unmapped fields (raw_data) so the modal shows the
	// whole assessor record, not just the typed columns the sync mapped.
	for (const row of rawSitusRows(situs.raw_data)) {
		rows.push(row);
	}
	return rows;
});
</script>
